/**
 * Lazy load scripts.
 *
 * @module app/components/LazyLoadingScript
 */
import _ from 'lodash';
import jQuery from 'jquery';

// jshint maxstatements:false

/**
 * All already loaded and executed resources.
 *
 * @type {Object}
 */
var loaded = {};

/**
 * All resources with theirs callback that are actually being processed
 *
 * @type {Object}
 */
var queues = {};

/**
 * Return noop function if given callback is falsy.
 *
 * @param {Function} callback
 *
 * @returns {Function} either provided callback, if it is defined;
 *                     noop function otherwise.
 */
var defaultCallback = function defaultCallback(callback) {

    return callback || (() => {});
};

/**
 * Should url be resolved immediately
 *
 * @param {String} url
 * @param {Function} loadedVerifier
 *
 * @returns {Boolean}
 */
var shouldResolve = function shouldLoad(url, loadedVerifier) {

    return !url || isLoaded(url, loadedVerifier);
};

/**
 * Get url string, extract it if provided url is an object
 *
 * @param {String|Object} url
 *
 * @returns {String}
 */
var getUrl = function getUrl(url) {

    return _.isObject(url) ? url.url : url;
};

/**
 * Load and execute provided js url.
 * Load every url only once.
 * Pass 3rd argument to check if script was loaded from outside of module.
 *
 * @param {String|Object} url Address of the js asset
 * @param {Function} [callback] Called after asset is downloaded and executed
 * @param {Function} [loadedVerifier] Extra function checking if asset is already loaded
 *
 * @returns {void}
 */
var loadScript = function loadScript(url, callback, loadedVerifier) {

    callback = defaultCallback(callback);

    if (shouldResolve(getUrl(url), loadedVerifier)) {

        callback();
        return;
    }

    processScriptLoading(url, callback);

};

/**
 * Load bunch of assets one by one - always wait for previous asset to be loaded before loading next.
 * Callback is called when all assets are loaded.
 * Verifiers are mapped to provided urls in order.
 *
 * @param {String[]|Object[]} urls Array of assets
 * @param {Function} [callback] Called after all assets are downloaded and executed
 * @param {Function[]} [loadedVerifiers] Extra function checking if asset is already loaded
 *
 * @returns {void}
 */
function loadAllOneByOne(urls, callback = () => {}, loadedVerifiers = []) {

    const [firstUrl, ...restUrls] = urls;
    const [firstVerifiers, ...restVerifiers] = loadedVerifiers;

    urls.length
        ? loadScript(firstUrl, () => loadAllOneByOne(restUrls, callback, restVerifiers), firstVerifiers)
        : callback();
}

/**
 * Load bunch of assets.
 * Callback is called when all assets are loaded.
 * Verifiers are mapped to provided urls in order.
 *
 * @param {Object[]|String[]} urls Array of assets
 * @param {Function} [callback] Called after all assets are downloaded and executed
 * @param {Function[]} [loadedVerifiers] Extra function checking if assets are already loaded
 *
 * @returns {void}
 */
var loadAll = function loadAll(urls, callback, loadedVerifiers) {

    callback = defaultCallback(callback);

    var loaded = 0;

    /**
     * Callback called after every loaded file
     *
     * @returns {void}
     */
    var loadOneCallback = function loadOneCallback() {

        loaded += 1;

        if (loaded === urls.length) {

            callback();
        }
    };

    /**
     * Get verifier function by index
     *
     * @param {Number} index
     *
     * @returns {Function}
     */
    var getVerifier = function getVerifier(index) {

        return loadedVerifiers ? loadedVerifiers[index] : null;
    };

    /**
     * Load script for provided url/index pair
     *
     * @param {String} url Assets url
     * @param {Number} index assets index to be mapped to verifier
     *
     * @returns {void}
     */
    var loadOne = function loadOne(url, index) {

        loadScript(url, loadOneCallback, getVerifier(index));
    };

    _.forEach(urls, loadOne);
};

/**
 * Put url in queue and trigger request if needed
 *
 * @param {String} url
 * @param {Function} callback
 */
var processScriptLoading = function processScriptLoading(url, callback) {

    var urlString = getUrl(url);

    if (isQueued(urlString)) {

        addToQueue(urlString, callback);
    } else {

        addToQueue(urlString, callback);

        makeRequest(url);
    }
};

/**
 * Append link element to head with provided url
 *
 * @param {String} url
 *
 * @returns {void}
 */
var appendLink = function appendLink(url, onLoad) {

    var link = jQuery('<link rel="stylesheet" type="text/css" href="' + url + '" />').prependTo('head');
    if (onLoad) {
        link.on('load', onLoad);
    }
};

/**
 * Make ajax request for provided url
 * On respond resolve queue of callbacks for given url
 *
 * @param {Object|String} url
 */
var makeRequest = function makeRequest(url) {

    var urlString = getUrl(url);

    /**
     * It's called after loading and executing asset
     */
    var successCallback = function successCallback() {

        setLoaded(urlString);
        resolveQueue(urlString);
    };

    if (_.isObject(url) && url.type === 'css') {

        appendLink(urlString, url.onLoad);
        setLoaded(urlString);
        resolveQueue(urlString);
    } else {

        jQuery.ajax({
            dataType: 'script',
            cache: true,
            url: urlString,
        }).done(successCallback)
            .fail(successCallback); // in case of fail we need to succeed to prevent infinite loading
    }

};

/**
 * Set url as successfully loaded
 *
 * @param {String} url
 */
var setLoaded = function setLoaded(url) {

    loaded[url] = true;
};

/**
 * Add url with its callback to queue
 *
 * @param {String} url
 * @param {Function} callback
 */
var addToQueue = function addToQueue(url, callback) {

    queues[url] = queues[url] || [];

    queues[url].push(callback);
};

/**
 * Check is provided url is already in queue
 *
 * @param {String} url
 *
 * @returns {Boolean}
 */
var isQueued = function isQueued(url) {

    return !_.isEmpty(queues[url]);
};

/**
 * Check if provided url is loaded or provided loadedVerifier function is fulfilled
 *
 * @param {String} url Url to check
 * @param {Function} loadedVerifier optional loaded condition
 *
 * @returns {Boolean}
 */
var isLoaded = function isLoaded(url, loadedVerifier) {

    return !!(loaded[url] || (loadedVerifier && loadedVerifier()));
};

/**
 * Run all functions in queue
 *
 * @param {String} url Queue key
 *
 * @returns {void}
 */
var resolveQueue = function resolveQueue(url) {

    /**
     * Run callback
     *
     * @param {Function} callback
     */
    var resolveCallback = function resolveCallback(callback) {

        callback();
    };

    _.each(queues[url], resolveCallback);

    delete queues[url];
};

/**
 * Converts given css url to object usable by this module.
 *
 * @param {String} url  css asset url.
 *
 * @returns {Object} with 'type' & 'url' props.
 *
 * @public
 */
const toCssAsset = url => ({
    type: 'css',
    url,
});


/**
 * Public functions
 *
 * @type {Object}
 */
var api = {
    load: loadScript,
    loadAll,
    loadAllOneByOne,
    toCssAsset,
};

export default api;
